import { FC } from 'react';

import { Form, Formik } from 'formik';

import {
  Box,
  Text,
  Flex,
  Input,
  Button,
  Divider,
  FormLabel,
  IconButton,
  FormControl,
  Heading,
} from '@chakra-ui/react';
import { HiMinusCircle } from 'react-icons/hi';

import { useSKU, useSites } from 'hooks';

import getSortedData from 'utils/getSortedData';

import { TotalItemsInput } from './TotalItemsInput';

import FormikInput from 'components/primitives/FormikInput';
import FormikTextArea from 'components/primitives/FormikTextArea';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import { Order } from 'types/order';

import { getInitialValues, validationSchema } from '../EditOrderModal.utils';
import { SiteFilterableSelect } from 'components/modules/SiteFilterableSelect';
import { calculateItemsPerPackage } from 'utils/itemsHelper';
import moment from 'moment';

type Props = {
  order: Order;
  isLoading: boolean;
  onCancel: () => void;
  isCustomerRequest?: boolean;
  onSubmit: (values: any) => void;
};

const px = { base: '20px', md: '100px' };

const DayOfTheWeekMap: { [key: string]: string } = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

export const EditForm: FC<Props> = ({
  order,
  onCancel,
  onSubmit,
  isLoading,
  isCustomerRequest,
}) => {
  const { data: sites = [], isLoading: isLoadingSites } = useSites();

  const { data: skuData } = useSKU('package');

  return (
    <Formik
      initialValues={getInitialValues(order)}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({ isValid, values, setFieldValue }) => {
        const _site: any = sites?.find((s) => s._id === values.site);
        const isDaily = _site?.deliveryFrequency === 'Daily';
        const _deliveryDayOfTheWeek = _site?.deliveryDayOfTheWeek;

        let deliveryDayOfTheWeek = '';

        if (isDaily) {
          deliveryDayOfTheWeek = '';
        } else if (Array.isArray(_deliveryDayOfTheWeek)) {
          if (_deliveryDayOfTheWeek?.length === 1) {
            deliveryDayOfTheWeek = _deliveryDayOfTheWeek?.[0];
          } else {
            deliveryDayOfTheWeek = _deliveryDayOfTheWeek
              ?.map((day) => DayOfTheWeekMap[day])
              ?.join(', ');
          }
        }

        const frequency = _site?.deliveryFrequency;

        let deliveryFrequency = frequency ? `(${frequency})` : '';

        if (isDaily) {
          deliveryFrequency = frequency;
        }

        return (
          <Form>
            <Flex
              shadow="lg"
              w="100%"
              py="30px"
              align="center"
              justify="space-between"
              px={px}>
              <Heading fontSize="24px" fontWeight="600">
                Order Details
              </Heading>
            </Flex>
            <Flex direction="column" px={px} mt="20px">
              <Flex alignItems="flex-end">
                <SiteFilterableSelect
                  isReadOnly={isCustomerRequest}
                  containerProps={{ flex: 1 }}
                  label="Site(*)"
                  name="site"
                  labelProps={labelProps}
                  isRequired={false}
                  elementContainerProps={{ maxW: '250px' }}
                />

                <Box h="20px" w="20px" />
                <FormControl style={{ flex: 1 }}>
                  <FormLabel
                    htmlFor={'deliveryDayOfTheWeek'}
                    fontSize="12px"
                    color="#afafaf"
                    fontWeight="bold"
                    textTransform="capitalize">
                    Delivery Date of the week
                  </FormLabel>
                  <FormikInput
                    name="deliveryDayOfTheWeek"
                    value={
                      `${deliveryDayOfTheWeek} ${deliveryFrequency}` || '0'
                    }
                    isReadOnly
                  />
                </FormControl>

                <Box h="20px" w="20px" />
                <FormikDatepicker
                  name="deliveryAt"
                  label="Delivery Date(*)"
                  containerProps={{ flex: 1 }}
                  labelProps={labelProps}
                  minDate={null}
                  formatInUTC
                />
                <Box h="82px" />
              </Flex>
              <Flex justify="space-between" gap={4}>
                <Box mt="8" w="100%">
                  <FormikTextArea
                    name="notes"
                    label="Customer Order Notes"
                    value={values.notes}
                    description="Can also include notes to drivers (such as equipment) as this is visible to driver in app during delivery."
                  />
                </Box>
                <Box mt="8" w="100%">
                  <FormikTextArea
                    name="notes"
                    label="Site Delivery instructions"
                    description="Visible every delivery in app. Edit in customer details."
                    value={_site?.deliveryInstructions}
                    bg="red"
                    isReadOnly
                  />
                </Box>
              </Flex>

              <Flex direction="column" maxH="200px" overflow="auto" mt="10px">
                {values.details.map((d, i) => {
                  const _sku = skuData?.find((s) => s._id === d.sku);
                  const itemsPerPackage = calculateItemsPerPackage(_sku);
                  return (
                    <Flex
                      key={`${d.sku}-${d.sku}-${i}`}
                      gap={4}
                      position="relative"
                      alignItems="flex-end"
                      justifyContent="space-between">
                      <PackageSiteSelect index={i} siteId={values.site} />
                      <FormikInput
                        id={`count-${i}`}
                        type="number"
                        name={`details[${i}].casesCount`}
                        label="Case Amount(*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1 }}
                      />

                      <FormikInput
                        name="itemsPerPackage"
                        label="Items per Package"
                        labelProps={labelProps}
                        containerProps={{ flex: 1 }}
                        isReadOnly
                        value={itemsPerPackage}
                      />

                      <TotalItemsInput i={i} washHubId={order?.washHub?._id} />
                      {values.details.length > 1 && (
                        <IconButton
                          ml="-10px"
                          aria-label="remove-item"
                          icon={<HiMinusCircle size="25px" fill="#E54444" />}
                          size="sm"
                          variant="ghost"
                          mb={1}
                          onClick={() => {
                            setFieldValue('details', [
                              ...(values?.details as any[])?.filter(
                                (_, index) => index !== i,
                              ),
                            ]);
                          }}
                        />
                      )}
                    </Flex>
                  );
                })}
              </Flex>

              <Flex flex={1} mt="10px" w="100%">
                {values?.details?.length <= 9 && (
                  <Button
                    w="100%"
                    size="lg"
                    borderRadius="5px"
                    variant="outline"
                    bg="primary.900"
                    onClick={() =>
                      setFieldValue('details', [
                        ...values.details,
                        {
                          sku: '',
                          casesCount: '',
                        },
                      ])
                    }>
                    Add SKU (max 10)
                  </Button>
                )}
              </Flex>
              <Flex my="30px" flexDirection="row" justifyContent="center">
                <Button
                  w="134px"
                  onClick={() => {
                    onCancel();
                  }}
                  isDisabled={isLoading}
                  variant="outline">
                  Cancel
                </Button>
                <Divider w="10px" />
                <Button
                  type="submit"
                  w="134px"
                  isLoading={isLoading}
                  isDisabled={!isValid}>
                  Update
                </Button>
              </Flex>
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};

const PackageSiteSelect: FC<{ index: number; siteId: string }> = ({
  index,
  siteId,
}) => {
  const { isLoading, data: sku = [] } = useSKU('package', undefined, siteId);

  return (
    <FormikSelectInput
      id={`sku-${index}`}
      placeholder="Select SKU"
      label="Package SKU(*)"
      isLoading={isLoading}
      containerProps={{ flex: 1 }}
      name={`details[${index}].sku`}
      labelProps={labelProps}>
      {getSortedData(sku)?.map((p) => (
        <option key={p._id} value={p._id}>
          {p?.name || ''}
        </option>
      ))}
    </FormikSelectInput>
  );
};
